// src/contexts/AuthContext.tsx 完整檔案
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  User,
  subscribeToAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
  logout,
  sendPasswordReset,
  ensureUserDocument,
  shouldShowPersonalization,
} from '../services/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { logDev, logInfo, logWarn } from '../utils/logger';
import { analyticsEvents, setAnalyticsUserId } from '../utils/analytics';

// ============================================
// 離線快取常數和工具函數
// ============================================
const CACHED_USER_KEY = 'orlo_cached_user';
const CACHED_USER_VERSION = 2; // 升級版本號以支援 onboarding 快取

interface CachedUser {
  version: number;
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  cachedAt: number;

  // onboarding 狀態快取
  onboarding?: {
    personalization: boolean;
    cachedAt: number;  // 單獨的快取時間戳
  };
}

/**
 * 將用戶基本資訊存入 localStorage
 * 用於離線啟動時快速恢復用戶狀態
 * 同時快取 onboarding 狀態以支援離線判斷
 * 離線模式下保留現有 onboarding 資訊，避免覆蓋
 */
const cacheUserToLocalStorage = async (user: User | null): Promise<void> => {
  try {
    if (user) {
      // 先讀取現有快取，避免覆蓋 onboarding 資訊
      let onboardingCache: { personalization: boolean; cachedAt: number } | undefined;

      // 先嘗試從現有快取獲取 onboarding 資訊（作為 fallback）
      try {
        const existingCache = localStorage.getItem(CACHED_USER_KEY);
        if (existingCache) {
          const parsed = JSON.parse(existingCache);
          if (parsed.uid === user.uid && parsed.onboarding) {
            const existingOnboarding = parsed.onboarding;
            onboardingCache = existingOnboarding;
            logDev('Auth', '保留現有 localStorage onboarding 資訊', {
              personalization: existingOnboarding.personalization
            });
          }
        }
      } catch (error) {
        logDev('Auth', '讀取現有快取失敗，將嘗試從 Firestore 獲取', error);
      }

      // 嘗試從 Firestore 讀取最新 onboarding 狀態（優先級高於快取）
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const onboarding = userData?.onboarding || {};
          onboardingCache = {
            personalization: onboarding.personalization === true,
            cachedAt: Date.now()
          };
          logDev('Auth', 'Firestore onboarding 狀態已讀取（更新快取）', {
            personalization: onboardingCache.personalization
          });
        }
      } catch (error) {
        // Firestore 失敗時保留現有 onboarding 快取
        logWarn('Auth', 'Firestore onboarding 讀取失敗，保留現有快取', {
          hasExistingCache: !!onboardingCache,
          error
        });
        // 如果沒有現有快取且 Firestore 失敗，onboardingCache 保持 undefined
      }

      const cached: CachedUser = {
        version: CACHED_USER_VERSION,
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        cachedAt: Date.now(),
        onboarding: onboardingCache // 可能來自現有快取或 Firestore
      };

      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(cached));
      logDev('Auth', '用戶資訊已快取到 localStorage', {
        hasOnboarding: !!onboardingCache,
        source: onboardingCache ? 'firestore or existing cache' : 'none'
      });
    } else {
      localStorage.removeItem(CACHED_USER_KEY);
      logDev('Auth', '已清除 localStorage 用戶快取');
    }
  } catch (error) {
    logWarn('Auth', 'localStorage 快取操作失敗', error);
  }
};

/**
 * 從 localStorage 讀取快取的用戶資訊
 * 用於離線啟動時立即顯示用戶狀態
 */
const getCachedUserFromLocalStorage = (): CachedUser | null => {
  try {
    const cached = localStorage.getItem(CACHED_USER_KEY);
    if (!cached) return null;

    const parsed: CachedUser = JSON.parse(cached);

    // 版本檢查
    if (parsed.version !== CACHED_USER_VERSION) {
      localStorage.removeItem(CACHED_USER_KEY);
      logDev('Auth', '快取版本不匹配，已清除舊快取', {
        cachedVersion: parsed.version,
        currentVersion: CACHED_USER_VERSION
      });
      return null;
    }

    // 快取有效期：7 天
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - parsed.cachedAt > maxAge) {
      localStorage.removeItem(CACHED_USER_KEY);
      logDev('Auth', '用戶快取已過期，已清除');
      return null;
    }

    return parsed;
  } catch (error) {
    logWarn('Auth', '讀取 localStorage 快取失敗', error);
    return null;
  }
};

/**
 * 將快取的用戶資訊轉換為類似 Firebase User 的物件
 * 注意：這是精簡版，只包含基本資訊
 */
const createUserFromCache = (cached: CachedUser): User => {
  return {
    uid: cached.uid,
    email: cached.email,
    displayName: cached.displayName,
    photoURL: cached.photoURL,
    // 標記為快取來源（可選，用於 UI 提示）
    emailVerified: false,
    isAnonymous: false,
    metadata: {},
    providerData: [],
    refreshToken: '',
    tenantId: null,
    delete: async () => { throw new Error('Cached user cannot delete'); },
    getIdToken: async () => { throw new Error('Cached user cannot get token'); },
    getIdTokenResult: async () => { throw new Error('Cached user cannot get token result'); },
    reload: async () => { throw new Error('Cached user cannot reload'); },
    toJSON: () => cached,
    providerId: 'cached'
  } as unknown as User;
};

// Onboarding 狀態介面
interface OnboardingStatus {
  loading: boolean;
  needsPersonalization: boolean;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: () => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logoutUser: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  error: string | null;
  setError: (error: string | null) => void;

  // Onboarding 狀態管理
  onboardingStatus: OnboardingStatus;
  refreshOnboardingStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ============================================
  // 離線快取：初始化時先從 localStorage 讀取
  // ============================================
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const cached = getCachedUserFromLocalStorage();
    if (cached) {
      logInfo('Auth', '從 localStorage 快取恢復用戶狀態（離線優先）', { uid: cached.uid });
      return createUserFromCache(cached);
    }
    return null;
  });

  // 如果有快取用戶，則立即結束 loading
  const [loading, setLoading] = useState<boolean>(() => {
    const cached = getCachedUserFromLocalStorage();
    return !cached; // 有快取就不 loading
  });

  const [error, setError] = useState<string | null>(null);

  // 初始化時從 localStorage 讀取 onboarding 狀態
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus>(() => {
    const cached = getCachedUserFromLocalStorage();
    if (cached?.onboarding) {
      logDev('Auth', '從 localStorage 快取恢復 onboarding 狀態', {
        personalization: cached.onboarding.personalization
      });
      return {
        loading: false,
        needsPersonalization: !cached.onboarding.personalization,
      };
    }
    return {
      loading: false,
      needsPersonalization: false,
    };
  });

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = subscribeToAuthChanges(async (user) => {
      // 更新用戶狀態並同步到 localStorage
      setCurrentUser(user);
      setLoading(false);
      cacheUserToLocalStorage(user).catch(error => {
        // 快取失敗不影響主流程
        logWarn('Auth', '後台快取失敗', error);
      });

      if (user) {
        // 設定 Analytics User ID（不包含敏感資料）
        setAnalyticsUserId(user.uid);

        // 異步處理用戶文檔確保
        ensureUserDocument(user)
          .then(result => {
            // 日誌記錄（開發環境）
            if (process.env.NODE_ENV === 'development') {
              if (result.isNewUser) {
                logInfo('Auth', '新用戶註冊，訂閱初始化完成');
              } else {
                logDev('Auth', '訂閱狀態檢查完成');
              }
            }

            // 事件追蹤（所有環境，analytics.ts 內部會判斷是否發送）
            if (result.isNewUser) {
              analyticsEvents.userSignUp();
            } else {
              analyticsEvents.userLogin();
            }
          })
          .catch(error => {
            logWarn('Auth', '用戶文檔確保失敗，但不影響登入', error);
          });

        // 登入時檢查 onboarding 狀態（一次性）
        checkOnboardingStatus(user);
      } else {
        // 用戶登出時清除 onboarding 狀態
        logDev('Auth', '用戶登出，清除 onboarding 狀態');
        setOnboardingStatus({
          loading: false,
          needsPersonalization: false,
        });
      }
    });

    // Cleanup function
    return unsubscribe;
  // checkOnboardingStatus 用 useCallback 穩定，intentional empty deps 避免重複訂閱
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 網路狀態監聽，避免網路問題導致意外登出
  useEffect(() => {
    const handleNetworkChange = () => {
      if (navigator.onLine && currentUser) {
        // 網路恢復時，嘗試刷新用戶狀態，但不強制重新登入
        logInfo('Auth', '網路已恢復，刷新用戶狀態');
        // 使用 Firebase Auth 的內建方法嘗試重新載入用戶資訊
        if (auth.currentUser) {
          auth.currentUser.reload().catch(error => {
            logWarn('Auth', '用戶狀態刷新失敗，保持當前狀態', error);
            // 不做任何處理，保持當前的登入狀態
          });
        }
      }
    };

    const handleOffline = () => {
      logInfo('Auth', '網路已斷線，切換到離線模式');
    };

    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleNetworkChange);
      window.removeEventListener('offline', handleOffline);
    };
  }, [currentUser]);


  // Login
  const login = async (email: string, password: string) => {
    try {
      setError(null);
      return await loginWithEmail(email, password);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      const errorMessage = code === 'auth/invalid-credential'
        ? 'Incorrect email or password'
        : 'Login failed. Please try again later.';
      setError(errorMessage);
      throw err;
    }
  };

  // Google login
  const loginWithGoogleHandler = async () => {
    try {
      setError(null);
      return await loginWithGoogle();
    } catch (err: unknown) {
      setError('Google login failed. Please try again later.');
      throw err;
    }
  };

  // Register
  const register = async (email: string, password: string) => {
    try {
      setError(null);
      return await registerWithEmail(email, password);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/email-already-in-use') {
        setError('This email is already in use');
      } else if (code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters');
      } else {
        setError('Registration failed. Please try again later.');
      }
      throw err;
    }
  };

  // Logout
  const logoutUser = async () => {
    try {
      setError(null);

      // 追蹤登出事件
      analyticsEvents.userLogout();
      // 清除 Analytics User ID
      setAnalyticsUserId(null);

      // 清除離線快取
      cacheUserToLocalStorage(null).catch(error => {
        // 快取清除失敗不影響登出流程
        logWarn('Auth', '清除快取失敗', error);
      });

      // 清除 onboarding 狀態
      setOnboardingStatus({
        loading: false,
        needsPersonalization: false,
      });

      await logout();
    } catch (err) {
      setError('Logout failed. Please try again later.');
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordReset(email);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/user-not-found') {
        setError('No user found with this email');
      } else {
        setError('Password reset failed. Please try again later.');
      }
      throw err;
    }
  };

  // Refresh onboarding status manually
  const refreshOnboardingStatus = async () => {
    if (!currentUser) return;

    logDev('Auth', '手動刷新 onboarding 狀態');
    await checkOnboardingStatus(currentUser);
  };

  // 檢查 onboarding 狀態
  const checkOnboardingStatus = useCallback(async (user: User) => {
    try {
      setOnboardingStatus(prev => ({ ...prev, loading: true }));
      logDev('Auth', '開始檢查 onboarding 狀態', { userId: user.uid });

      const needsPersonalization = await shouldShowPersonalization(user.uid);

      setOnboardingStatus({
        loading: false,
        needsPersonalization,
      });

      logDev('Auth', 'Onboarding 狀態檢查完成', { needsPersonalization });

    } catch (error) {
      logWarn('Auth', '檢查 onboarding 狀態失敗', error);
      setOnboardingStatus(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const value = {
    currentUser,
    loading,
    login,
    loginWithGoogle: loginWithGoogleHandler,
    register,
    logoutUser,
    resetPassword,
    error,
    setError,

    // Onboarding 相關屬性
    onboardingStatus,
    refreshOnboardingStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};